import React, { useEffect, useState } from 'react';
import { posApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const FIELD_INFO = {
    points_per_currency: {
        label: 'Points earned per ₦1 spent',
        hint: 'e.g. 0.01 = customers earn 1 point per ₦100 spent',
        type: 'number',
        step: '0.0001',
        min: '0',
    },
    points_per_redemption_unit: {
        label: 'Points required per ₦1 off',
        hint: 'e.g. 100 = 100 points = ₦1 discount at checkout',
        type: 'number',
        step: '0.0001',
        min: '1',
    },
    min_redemption_points: {
        label: 'Minimum points before redemption allowed',
        hint: 'Customer must have at least this many points to redeem any discount.',
        type: 'number',
        step: '1',
        min: '0',
    },
    max_redemption_percent: {
        label: 'Maximum % of transaction payable with points',
        hint: 'e.g. 0.5 = customers can pay at most 50% of the bill with loyalty points',
        type: 'number',
        step: '0.01',
        min: '0',
        max: '1',
    },
    welcome_points: {
        label: 'Welcome bonus points for new customers',
        hint: 'Points automatically awarded when a new customer is registered.',
        type: 'number',
        step: '1',
        min: '0',
    },
    expiry_days: {
        label: 'Points expiry (days, 0 = never expire)',
        hint: 'Set to 0 to disable point expiry.',
        type: 'number',
        step: '1',
        min: '0',
    },
};

export default function LoyaltyConfigPage() {
    const { user } = useAuth();
    const isReadOnly = user?.role !== 'admin' && user?.role !== 'manager';

    const [form, setForm] = useState({
        enabled: false,
        points_per_currency: 0.01,
        points_per_redemption_unit: 100,
        min_redemption_points: 500,
        max_redemption_percent: 0.5,
        welcome_points: 0,
        expiry_days: 0,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        posApi.getLoyaltyConfig().then((loyaltyRes) => {
            setLoading(false);
            if (loyaltyRes.success && loyaltyRes.data) {
                setForm({
                    enabled: loyaltyRes.data.enabled ?? false,
                    points_per_currency: loyaltyRes.data.points_per_currency ?? 0.01,
                    points_per_redemption_unit: loyaltyRes.data.points_per_redemption_unit ?? 100,
                    min_redemption_points: loyaltyRes.data.min_redemption_points ?? 500,
                    max_redemption_percent: loyaltyRes.data.max_redemption_percent ?? 0.5,
                    welcome_points: loyaltyRes.data.welcome_points ?? 0,
                    expiry_days: loyaltyRes.data.expiry_days ?? 0,
                });
            }
        });
    }, []);

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setSaved(false);
    };

    const handleSave = async () => {
        setError('');
        setSaving(true);
        const payload = { ...form };
        // Coerce numeric fields
        for (const key of ['points_per_currency', 'points_per_redemption_unit', 'min_redemption_points', 'max_redemption_percent', 'welcome_points', 'expiry_days']) {
            payload[key] = Number(payload[key]);
        }
        const loyaltyRes = await posApi.updateLoyaltyConfig(payload);
        setSaving(false);
        if (loyaltyRes.success) {
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } else {
            setError(typeof loyaltyRes.error === 'string' ? loyaltyRes.error : 'Failed to save');
        }
    };

    // Live preview: example purchase of ₦5000
    const exampleAmount = 5000;
    const examplePoints = Math.floor(exampleAmount * Number(form.points_per_currency));
    const exampleRedemptionValue = (500 / Number(form.points_per_redemption_unit)).toFixed(2);

    if (loading) return <div className="page-body"><p>Loading…</p></div>;

    return (
        <div className="page-body">
            <div className="page-header" style={{ marginBottom: 20 }}>
                <h2>Loyalty Program</h2>
                <p>Configure how customers earn and redeem loyalty points at checkout.</p>
            </div>

            <div className="loyalty-config-grid">
                {/* Settings panel */}
                <div className="panel" style={{ padding: 24 }}>
                    <div className="loyalty-enable-row">
                        <div>
                            <strong>Enable Loyalty Program</strong>
                            <p className="muted-sm">When enabled, customers earn points on every purchase and can redeem them for discounts.</p>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={form.enabled}
                                onChange={(e) => handleChange('enabled', e.target.checked)}
                                disabled={isReadOnly}
                            />
                            <span className="toggle-track" />
                        </label>
                    </div>

                    {Object.entries(FIELD_INFO).map(([key, info]) => (
                        <div className="loyalty-field" key={key} style={{ opacity: form.enabled ? 1 : 0.45 }}>
                            <label className="field-label">{info.label}</label>
                            <input
                                type={info.type}
                                step={info.step}
                                min={info.min}
                                max={info.max}
                                value={form[key]}
                                onChange={(e) => handleChange(key, e.target.value)}
                                disabled={isReadOnly || !form.enabled}
                            />
                            <p className="field-hint">{info.hint}</p>
                        </div>
                    ))}

                    {error && <div className="error-banner" style={{ marginTop: 12 }}>{error}</div>}

                    {!isReadOnly && (
                        <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <button className="btn" onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving…' : 'Save configuration'}
                            </button>
                            {saved && <span style={{ color: 'var(--success)', fontWeight: 600 }}>✓ Saved</span>}
                        </div>
                    )}

                    {isReadOnly && (
                        <p className="muted-sm" style={{ marginTop: 16 }}>Only admins and managers can edit loyalty settings.</p>
                    )}
                </div>

                {/* Preview panel */}
                {form.enabled && (
                    <div className="panel" style={{ padding: 24, alignSelf: 'start' }}>
                        <h4 style={{ margin: '0 0 16px' }}>Live Preview</h4>
                        <div className="loyalty-preview-example">
                            <div className="preview-scenario">
                                <span className="preview-label">Customer spends ₦{exampleAmount.toLocaleString()}</span>
                                <span className="preview-value">+{examplePoints} pts earned</span>
                            </div>
                            <div className="preview-scenario">
                                <span className="preview-label">With 500 pts to redeem</span>
                                <span className="preview-value">−₦{exampleRedemptionValue} off</span>
                            </div>
                            <div className="preview-scenario">
                                <span className="preview-label">Min to redeem</span>
                                <span className="preview-value">{Number(form.min_redemption_points).toLocaleString()} pts</span>
                            </div>
                            <div className="preview-scenario">
                                <span className="preview-label">Max redemption per sale</span>
                                <span className="preview-value">{Math.round(Number(form.max_redemption_percent) * 100)}% of total</span>
                            </div>
                            {Number(form.welcome_points) > 0 && (
                                <div className="preview-scenario">
                                    <span className="preview-label">New customer bonus</span>
                                    <span className="preview-value">+{Number(form.welcome_points)} welcome pts</span>
                                </div>
                            )}
                            {Number(form.expiry_days) > 0 && (
                                <div className="preview-scenario">
                                    <span className="preview-label">Points expire after</span>
                                    <span className="preview-value">{Number(form.expiry_days)} days</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
